/**
 * AI Social Media Generator Page
 * ===============================
 * 
 * Phase 1-2: AI image/video generation + social posting
 * Phase 5: Website Automation AI + Campaign Manager
 * 
 * Refactored: Feb 23, 2026 - Extracted reusable components
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Image as ImageIcon,
  Video,
  Sparkles,
  CreditCard,
  Download,
  Loader2,
  Coins,
  Zap,
  Gift,
  Share2,
  Clock,
  Check,
  Link2,
  Unlink,
  Send,
  Instagram,
  Facebook,
  Linkedin,
  Globe,
  Bot,
  Calendar,
  Target,
  Megaphone,
  Camera,
  Play,
  Eye,
  ChevronRight,
  ChevronLeft,
  Lock,
  X,
  FolderOpen,
  Trash2,
  TrendingUp,
  FlaskConical,
  CalendarClock,
  Search,
  Users,
  Building2,
  Crown,
  BarChart3,
  Wand2,
  FileSearch,
  Package,
  Upload,
  User,
  PlayCircle,
  RefreshCw,
  Mic,
} from "lucide-react";

// Import extracted components
import { 
  TikTokIcon,
  getToolCost as getToolCostFromSelector,
  LibraryTab,
  TrendingTab,
  AutomateTab,
  CampaignTab,
  ABTesterTab,
  SchedulerTab,
  AnalyzerTab,
  AgencyTab,
} from "./merchant/ai/components";
import { CREDIT_COSTS } from "./merchant/ai/constants";

const PLATFORM_ICONS = {
  instagram: Instagram,
  facebook: Facebook,
  linkedin: Linkedin,
  tiktok: TikTokIcon,
};

const AISocialPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  
  // Safe fetch helper to prevent "body stream already read" errors
  const safeFetch = async (url, options = {}) => {
    const res = await fetch(url, options);
    const text = await res.text();
    let data;
    try {
      data = JSON.parse(text);
    } catch {
      data = { detail: text || "Invalid response" };
    }
    return { ok: res.ok, status: res.status, data };
  };
  
  // Main view mode - ENHANCED with new tabs
  const [activeTab, setActiveTab] = useState("generate"); // generate, library, trending, abtester, scheduler, analyzer, agency
  
  // AI Tool Selection (merged TopView + Generate)
  const [aiTool, setAiTool] = useState("basic"); // basic, avatar, product_shoot, lip_sync, face_swap, image_to_video, viral_hooks
  
  // Credits
  const [credits, setCredits] = useState(0);
  const [packs, setPacks] = useState({});
  const [loadingCredits, setLoadingCredits] = useState(true);
  const [selectedPack, setSelectedPack] = useState("");
  const [buyingCredits, setBuyingCredits] = useState(false);
  
  // Generation state
  const [mode, setMode] = useState("image");
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState("professional");
  const [aspectRatio, setAspectRatio] = useState("1:1");
  const [videoDuration, setVideoDuration] = useState(4);
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const progressIntervalRef = useRef(null);
  
  // Image Upload for AI
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const uploadInputRef = useRef(null);
  
  // Prompt Preview (Phase 10)
  const [promptPreview, setPromptPreview] = useState(null);
  const [showPromptPreview, setShowPromptPreview] = useState(false);
  
  // Magic Prompt (Phase 9)
  const [magicPromptInput, setMagicPromptInput] = useState("");
  const [magicPromptVariants, setMagicPromptVariants] = useState([]);
  const [generatingMagic, setGeneratingMagic] = useState(false);
  const [showMagicModal, setShowMagicModal] = useState(false);
  const MAGIC_PROMPT_COST = 5;
  
  // TopView AI Tools (merged)
  const [viralHooks, setViralHooks] = useState([]);
  const [selectedHook, setSelectedHook] = useState(null);
  const [productName, setProductName] = useState("");
  const [keyBenefit, setKeyBenefit] = useState("");
  const [scene, setScene] = useState("store");
  const [motionType, setMotionType] = useState("zoom");
  const [voice, setVoice] = useState("nova");
  const [script, setScript] = useState("");
  const [topviewResult, setTopviewResult] = useState(null);
  const [showTopviewResult, setShowTopviewResult] = useState(false);
  
  // TopView credit costs
  const AVATAR_COST = 100;
  const PRODUCT_SHOOT_COST = 50;
  const LIP_SYNC_COST = 75;
  const FACE_SWAP_COST = 100;
  const IMAGE_TO_VIDEO_COST = 50;
  const VIRAL_HOOK_COST = 25;
  
  // Result
  const [result, setResult] = useState(null);
  const [resultType, setResultType] = useState(null);
  const [generationId, setGenerationId] = useState(null);
  
  // Promo code
  const [promoCode, setPromoCode] = useState("");
  const [redeemingPromo, setRedeemingPromo] = useState(false);
  
  // History
  const [history, setHistory] = useState([]);
  
  // Library (Phase 8)
  const [library, setLibrary] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryFilter, setLibraryFilter] = useState("all");
  const [libraryPage, setLibraryPage] = useState(1);
  const [libraryTotal, setLibraryTotal] = useState(0);
  const [libraryTotalPages, setLibraryTotalPages] = useState(0);
  const [librarySearch, setLibrarySearch] = useState("");
  
  // Social Media
  const [socialAccounts, setSocialAccounts] = useState({});
  const [showPostModal, setShowPostModal] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [postCaption, setPostCaption] = useState("");
  const [posting, setPosting] = useState(false);
  const [postHistory, setPostHistory] = useState([]);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [connectingPlatform, setConnectingPlatform] = useState(null);
  
  // Meta OAuth - Real connection
  const [showPageSelectModal, setShowPageSelectModal] = useState(false);
  const [availablePages, setAvailablePages] = useState([]);
  const [fbUserName, setFbUserName] = useState("");
  const [selectingPage, setSelectingPage] = useState(false);
  
  // Test Post (OAuth verification)
  const [testingPlatform, setTestingPlatform] = useState(null);
  
  // Website Automation (Phase 5)
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [automating, setAutomating] = useState(false);
  const [automationResult, setAutomationResult] = useState(null);
  const [automationStep, setAutomationStep] = useState(0);
  
  // Campaign Manager (Phase 5)
  const [campaignName, setCampaignName] = useState("");
  const [campaignGoal, setCampaignGoal] = useState("awareness");
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [campaignResult, setCampaignResult] = useState(null);
  
  // PHASE 9: TRENDING POSTS (FREE)
  const [trendingPosts, setTrendingPosts] = useState([]);
  const [loadingTrending, setLoadingTrending] = useState(false);
  
  // PHASE 9: A/B TESTER
  const [abTestName, setAbTestName] = useState("");
  const [abVariantA, setAbVariantA] = useState("");
  const [abVariantB, setAbVariantB] = useState("");
  const [abTestResults, setAbTestResults] = useState(null);
  const [runningAbTest, setRunningAbTest] = useState(false);
  
  // PHASE 9: SCHEDULER
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [selectedScheduleItem, setSelectedScheduleItem] = useState(null);
  
  // PHASE 9: COMPETITOR ANALYZER
  const [competitorUrl, setCompetitorUrl] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [competitorAnalysis, setCompetitorAnalysis] = useState(null);
  
  // PHASE 9: AGENCY MODE
  const [isAgencyMode, setIsAgencyMode] = useState(false);
  const [agencyClients, setAgencyClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  
  // Costs
  const IMAGE_COST = 5;
  const VIDEO_COST = 50;
  const AUTOMATION_COST = 200;
  const CAMPAIGN_COST = 250;
  const AB_TEST_COST = 100;
  const SCHEDULER_COST = 150;
  const ANALYZER_COST = 200;
  const AGENCY_COST = 500;

  // Check for payment status on return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const purchaseStatus = searchParams.get("purchase");
    const oauthSuccess = searchParams.get("oauth_success");
    const oauthError = searchParams.get("oauth_error");
    const pagesCount = searchParams.get("pages_count");
    
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else if (purchaseStatus === "cancelled") {
      toast.error("Purchase cancelled");
      navigate("/merchant/ai-social", { replace: true });
    }
    
    // Handle OAuth callback
    if (oauthSuccess === "facebook") {
      if (parseInt(pagesCount) > 0) {
        toast.success("Facebook connected! Select a Page to complete setup.");
        fetchAvailablePages();
      } else {
        toast.error("No Facebook Pages found. Please create a Page first.");
        navigate("/merchant/ai-social", { replace: true });
      }
    } else if (oauthSuccess === "tiktok") {
      toast.success("TikTok connected successfully!");
      fetchSocialAccounts();
      navigate("/merchant/ai-social", { replace: true });
    } else if (oauthSuccess === "linkedin") {
      toast.success("LinkedIn connected successfully!");
      fetchSocialAccounts();
      navigate("/merchant/ai-social", { replace: true });
    } else if (oauthError) {
      toast.error(`OAuth failed: ${oauthError.replace(/_/g, ' ')}`);
      navigate("/merchant/ai-social", { replace: true });
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;
    
    if (attempts >= maxAttempts) {
      toast.error("Payment status check timed out. Please refresh the page.");
      navigate("/merchant/ai-social", { replace: true });
      return;
    }
    
    try {
      const res = await fetch(`${API}/ai/credits/payment-status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error("Failed to check payment status");
      
      const data = await res.json();
      
      if (data.payment_status === "paid") {
        setCredits(data.new_balance || credits);
        toast.success(data.message || "Credits added!");
        navigate("/merchant/ai-social", { replace: true });
        return;
      } else if (data.status === "expired") {
        toast.error("Payment session expired. Please try again.");
        navigate("/merchant/ai-social", { replace: true });
        return;
      }
      
      toast.loading("Checking payment status...", { id: "payment-check" });
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (err) {
      console.error("Error checking payment status:", err);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        toast.error("Error checking payment. Please refresh the page.");
        navigate("/merchant/ai-social", { replace: true });
      }
    }
  };

  useEffect(() => {
    fetchCredits();
    fetchPacks();
    fetchHistory();
    fetchSocialAccounts();
    fetchPostHistory();
    fetchTrendingPosts();
    fetchScheduledPosts();
    fetchViralHooks();
  }, []);

  // Fetch viral hooks for TopView
  const fetchViralHooks = async () => {
    try {
      const res = await fetch(`${API}/topview/viral-hooks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setViralHooks(data.hooks || []);
      }
    } catch (error) {
      console.error("Failed to fetch viral hooks:", error);
    }
  };

  // Fetch library when tab changes or filter/page changes
  useEffect(() => {
    if (activeTab === "library") {
      fetchLibrary();
    }
  }, [activeTab, libraryFilter, libraryPage]);

  // PHASE 9: Fetch trending posts (FREE daily)
  const fetchTrendingPosts = async () => {
    setLoadingTrending(true);
    try {
      const res = await fetch(`${API}/ai/trending?platform=all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        // Transform API data to UI format
        const formattedPosts = data.trending_topics.slice(0, 6).map((topic, idx) => ({
          id: `trend_${idx}`,
          topic: topic.topic,
          hashtags: topic.hashtags,
          caption: `Create engaging content about ${topic.topic}. ${topic.engagement_rate} engagement rate with ${topic.posts_today.toLocaleString()} posts today.`,
          engagement: `${topic.engagement_rate} engagement`,
          image_url: `https://placehold.co/400x400/${['7c3aed', 'ec4899', '06b6d4', 'f59e0b', '10b981', '6366f1'][idx % 6]}/white?text=${encodeURIComponent(topic.topic.split(' ')[0])}`
        }));
        setTrendingPosts(formattedPosts);
      } else {
        // Fallback to mock data
        setTrendingPosts([
          { id: "trend_1", topic: "AI in Business", hashtags: ["#AIMarketing", "#SmallBizAI"], caption: "Leverage AI for your business growth", engagement: "4.2% engagement", image_url: "https://placehold.co/400x400/7c3aed/white?text=AI" },
          { id: "trend_2", topic: "Behind the Scenes", hashtags: ["#BTS", "#DayInTheLife"], caption: "Show your authentic business journey", engagement: "5.8% engagement", image_url: "https://placehold.co/400x400/ec4899/white?text=BTS" },
          { id: "trend_3", topic: "Product Showcases", hashtags: ["#NewProduct", "#MustHave"], caption: "Highlight your latest offerings", engagement: "3.9% engagement", image_url: "https://placehold.co/400x400/06b6d4/white?text=Products" }
        ]);
      }
    } catch (err) {
      console.error(err);
      // Fallback
      setTrendingPosts([
        { id: "trend_1", topic: "AI in Business", hashtags: ["#AIMarketing", "#SmallBizAI"], caption: "Leverage AI for your business growth", engagement: "4.2% engagement", image_url: "https://placehold.co/400x400/7c3aed/white?text=AI" }
      ]);
    } finally {
      setLoadingTrending(false);
    }
  };

  // PHASE 9: A/B Test Runner
  const runAbTest = async () => {
    if (!abVariantA.trim() || !abVariantB.trim()) {
      toast.error("Please enter both variants");
      return;
    }
    if (credits < AB_TEST_COST) {
      toast.error(`Insufficient credits. Need ${AB_TEST_COST}, have ${credits}`);
      return;
    }

    setRunningAbTest(true);
    try {
      toast.loading("Creating A/B test...", { id: "abtest" });
      
      const res = await fetch(`${API}/ai/ab-test/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: `Test ${new Date().toLocaleString()}`,
          variant_a: { caption: abVariantA, hashtags: [] },
          variant_b: { caption: abVariantB, hashtags: [] },
          platform: "instagram",
          duration_hours: 24
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success("A/B test created! Tracking metrics...", { id: "abtest" });
        
        // Wait and fetch results
        await new Promise(resolve => setTimeout(resolve, 15000));
        
        const resultsRes = await fetch(`${API}/ai/ab-test/${data.test_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (resultsRes.ok) {
          const resultsData = await resultsRes.json();
          const test = resultsData.test;
          
          const results = {
            variantA: {
              prompt: abVariantA,
              clicks: test.variant_a.clicks || 0,
              impressions: test.variant_a.impressions || 0,
              ctr: test.variant_a.ctr || 0
            },
            variantB: {
              prompt: abVariantB,
              clicks: test.variant_b.clicks || 0,
              impressions: test.variant_b.impressions || 0,
              ctr: test.variant_b.ctr || 0
            },
            winner: test.winner || (test.variant_a.ctr > test.variant_b.ctr ? "A" : "B")
          };
          
          setAbTestResults(results);
          setCredits(c => c - AB_TEST_COST);
          toast.success(`Winner: Variant ${results.winner} (${results.winner === "A" ? results.variantA.ctr : results.variantB.ctr}% CTR)`, { id: "abtest" });
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to create A/B test", { id: "abtest" });
      }
    } catch (err) {
      toast.error("A/B test failed", { id: "abtest" });
    } finally {
      setRunningAbTest(false);
    }
  };

  // PHASE 9: Scheduler
  const fetchScheduledPosts = async () => {
    try {
      const res = await fetch(`${API}/ai/schedule/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setScheduledPosts(data.scheduled_posts || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const schedulePost = async (contentUrl, caption, platforms, scheduledTime) => {
    try {
      const res = await fetch(`${API}/ai/schedule/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          content_type: "image",
          content_url: contentUrl,
          caption: caption,
          hashtags: [],
          platforms: platforms,
          scheduled_time: scheduledTime
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchScheduledPosts();
        return true;
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to schedule post");
        return false;
      }
    } catch (err) {
      toast.error("Failed to schedule post");
      return false;
    }
  };

  const cancelScheduledPost = async (scheduleId) => {
    try {
      const res = await fetch(`${API}/ai/schedule/${scheduleId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success("Scheduled post cancelled");
        fetchScheduledPosts();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to cancel");
      }
    } catch (err) {
      toast.error("Failed to cancel scheduled post");
    }
  };

  const postScheduledNow = async (scheduleId) => {
    try {
      const res = await fetch(`${API}/ai/schedule/${scheduleId}/post-now`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchScheduledPosts();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to post");
      }
    } catch (err) {
      toast.error("Failed to post now");
    }
  };

  const schedulePostFromGeneration = async (generationId) => {
    if (!scheduleDate || !scheduleTime) {
      toast.error("Please select date and time");
      return;
    }
    
    toast.success("Post scheduled successfully!");
    setScheduledPosts(prev => [...prev, {
      id: `sched_${Date.now()}`,
      generation_id: generationId,
      scheduled_for: `${scheduleDate}T${scheduleTime}:00`,
      platform: selectedPlatform || "instagram",
      status: "pending"
    }]);
    setSelectedScheduleItem(null);
  };

  // PHASE 9: Competitor Analyzer
  const analyzeCompetitor = async () => {
    if (!competitorUrl.trim()) {
      toast.error("Please enter a competitor URL");
      return;
    }
    if (credits < ANALYZER_COST) {
      toast.error(`Insufficient credits. Need ${ANALYZER_COST}, have ${credits}`);
      return;
    }

    setAnalyzing(true);
    try {
      toast.loading("Analyzing competitor...", { id: "analyze" });
      
      // Simulate analysis (mock)
      await new Promise(resolve => setTimeout(resolve, 4000));
      
      const domain = competitorUrl.replace("https://", "").replace("http://", "").split("/")[0];
      
      setCompetitorAnalysis({
        url: competitorUrl,
        domain,
        metrics: {
          estimated_traffic: "50K-100K/mo",
          social_followers: "12.3K",
          post_frequency: "3x/week",
          avg_engagement: "4.2%"
        },
        weaknesses: [
          "Inconsistent posting schedule",
          "Low video content ratio",
          "Missing trending hashtags"
        ],
        beatThemPost: {
          caption: `Outperform ${domain}! Our exclusive offer beats the competition`,
          hashtags: "#BetterThanTheRest #TopChoice #QualityFirst",
          image_url: "https://placehold.co/600x600/10b981/white?text=Beat+Competition"
        }
      });
      
      setCredits(c => c - ANALYZER_COST);
      toast.success("Analysis complete!", { id: "analyze" });
    } catch (err) {
      toast.error("Analysis failed", { id: "analyze" });
    } finally {
      setAnalyzing(false);
    }
  };

  // PHASE 9: Magic Prompt - AI rewrites prompts for viral engagement
  const generateMagicPrompt = async () => {
    if (!magicPromptInput.trim()) {
      toast.error("Please enter something to transform");
      return;
    }
    if (credits < MAGIC_PROMPT_COST) {
      toast.error(`Insufficient credits. Need ${MAGIC_PROMPT_COST}, have ${credits}`);
      return;
    }

    setGeneratingMagic(true);
    try {
      toast.loading("✨ Creating magic...", { id: "magic" });
      
      const res = await fetch(`${API}/ai/magic-prompt`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          input_text: magicPromptInput,
          num_variants: 3
        })
      });

      if (res.ok) {
        const data = await res.json();
        setMagicPromptVariants(data.variants);
        setCredits(data.new_balance);
        toast.success("✨ Magic complete! Pick your favorite.", { id: "magic" });
      } else {
        const error = await res.json();
        toast.error(error.detail || "Magic failed", { id: "magic" });
      }
    } catch (err) {
      toast.error("Magic prompt error", { id: "magic" });
    } finally {
      setGeneratingMagic(false);
    }
  };

  const selectMagicVariant = (variant) => {
    const fullPrompt = variant.caption + "\n\n" + variant.hashtags.map(h => `#${h}`).join(" ");
    setPrompt(fullPrompt);
    setShowMagicModal(false);
    setMagicPromptVariants([]);
    setMagicPromptInput("");
    toast.success("Magic prompt applied! Ready to generate.");
  };

  // PHASE 10: Prompt Preview - See exact AI prompt before generation
  const previewPrompt = async () => {
    if (!prompt.trim()) {
      toast.error("Enter a prompt first to preview");
      return;
    }
    
    try {
      const tool = aiTool === "basic" ? (mode === "video" ? "img2video" : "product_shoot") : aiTool;
      const res = await fetch(`${API}/topview/prompt-preview?tool=${tool}&prompt=${encodeURIComponent(prompt)}&style=${style}&scene=${scene}&motion_type=${motionType}`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        setPromptPreview(data);
        setShowPromptPreview(true);
      } else {
        toast.error("Failed to preview prompt");
      }
    } catch (err) {
      console.error(err);
      toast.error("Preview error");
    }
  };

  const fetchLibrary = async () => {
    setLibraryLoading(true);
    try {
      const filterParam = libraryFilter !== "all" ? `&type_filter=${libraryFilter}` : "";
      const res = await fetch(`${API}/ai/library?page=${libraryPage}&limit=10${filterParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLibrary(data.generations);
        setLibraryTotal(data.total);
        setLibraryTotalPages(data.total_pages);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLibraryLoading(false);
    }
  };

  const deleteGeneration = async (generationId) => {
    if (!confirm("Are you sure you want to delete this generation?")) return;
    
    try {
      const res = await fetch(`${API}/ai/library/${generationId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success("Generation deleted");
        fetchLibrary();
      } else {
        toast.error("Failed to delete");
      }
    } catch (err) {
      toast.error("Error deleting generation");
    }
  };

  const downloadFromLibrary = async (item) => {
    // Use API download endpoint for reliable downloads
    try {
      toast.loading("Preparing download...", { id: "download" });
      
      const res = await fetch(`${API}/ai/download/${item.generation_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) {
        throw new Error("Download failed");
      }
      
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `veyronix_${item.generation_id}.${item.type === "image" ? "png" : "mp4"}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success("Downloaded!", { id: "download" });
    } catch (err) {
      console.error("Download error:", err);
      toast.error("Download failed. Please try again.", { id: "download" });
    }
  };

  const fetchCredits = async () => {
    try {
      const res = await fetch(`${API}/ai/credits/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCredits(data.balance);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingCredits(false);
    }
  };

  const fetchPacks = async () => {
    try {
      const res = await fetch(`${API}/ai/credits/packs`);
      if (res.ok) {
        const data = await res.json();
        setPacks(data.packs);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${API}/ai/generations/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setHistory(data.generations);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchSocialAccounts = async () => {
    try {
      // Fetch basic social accounts
      const res = await fetch(`${API}/social/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        let accounts = data.accounts;
        
        // Also fetch Meta status for live connection info
        const metaRes = await fetch(`${API}/oauth/meta/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (metaRes.ok) {
          const metaData = await metaRes.json();
          
          // Override Facebook status if live connected
          if (metaData.facebook?.connected && metaData.facebook?.connection_type === "live") {
            accounts.facebook = {
              ...accounts.facebook,
              connected: true,
              username: metaData.facebook.page_name,
              connection_type: "live",
              connected_at: metaData.facebook.connected_at
            };
          }
          
          // Override Instagram status if live connected
          if (metaData.instagram?.connected && metaData.instagram?.connection_type === "live") {
            accounts.instagram = {
              ...accounts.instagram,
              connected: true,
              username: metaData.instagram.username,
              connection_type: "live",
              connected_at: metaData.instagram.connected_at
            };
          }
        }
        
        setSocialAccounts(accounts);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchPostHistory = async () => {
    try {
      const res = await fetch(`${API}/social/posts/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPostHistory(data.posts);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const connectSocialAccount = async (platform) => {
    setConnectingPlatform(platform);
    
    // Use real OAuth for Facebook/Instagram
    if (platform === "facebook" || platform === "instagram") {
      try {
        const res = await fetch(`${API}/oauth/meta/facebook/auth?redirect_uri=${encodeURIComponent(window.location.origin + '/merchant/ai-social')}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          // Redirect to Facebook OAuth
          window.location.href = data.auth_url;
          return;
        } else {
          const error = await res.json();
          toast.error(error.detail || "Failed to start OAuth");
        }
      } catch (err) {
        toast.error("Error starting Facebook connection");
      } finally {
        setConnectingPlatform(null);
      }
      return;
    }
    
    // Use real OAuth for TikTok
    if (platform === "tiktok") {
      try {
        const res = await fetch(`${API}/oauth/tiktok/auth?redirect_uri=${encodeURIComponent(window.location.origin + '/merchant/ai-social')}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          window.location.href = data.auth_url;
          return;
        } else {
          const error = await res.json();
          toast.error(error.detail || "Failed to start TikTok OAuth");
        }
      } catch (err) {
        toast.error("Error starting TikTok connection");
      } finally {
        setConnectingPlatform(null);
      }
      return;
    }
    
    // Use real OAuth for LinkedIn
    if (platform === "linkedin") {
      try {
        const res = await fetch(`${API}/oauth/linkedin/auth?redirect_uri=${encodeURIComponent(window.location.origin + '/merchant/ai-social')}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          window.location.href = data.auth_url;
          return;
        } else {
          const error = await res.json();
          toast.error(error.detail || "Failed to start LinkedIn OAuth");
        }
      } catch (err) {
        toast.error("Error starting LinkedIn connection");
      } finally {
        setConnectingPlatform(null);
      }
      return;
    }
    
    // Fallback to mock for any other platforms
    try {
      const res = await fetch(`${API}/social/accounts/connect`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ platform })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchSocialAccounts();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Connection failed");
      }
    } catch (err) {
      toast.error("Error connecting account");
    } finally {
      setConnectingPlatform(null);
    }
  };
  
  const fetchAvailablePages = async () => {
    try {
      const res = await fetch(`${API}/oauth/meta/facebook/pages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setAvailablePages(data.pages || []);
        setFbUserName(data.fb_user_name || "");
        setShowPageSelectModal(true);
        navigate("/merchant/ai-social", { replace: true });
      } else {
        toast.error("Failed to load Facebook Pages");
        navigate("/merchant/ai-social", { replace: true });
      }
    } catch (err) {
      toast.error("Error loading pages");
      navigate("/merchant/ai-social", { replace: true });
    }
  };
  
  const selectFacebookPage = async (page) => {
    setSelectingPage(true);
    try {
      const res = await fetch(`${API}/oauth/meta/facebook/select-page`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          page_id: page.page_id,
          page_name: page.page_name,
          page_access_token: page.page_access_token,
          instagram_account_id: page.instagram_account_id,
          instagram_username: page.instagram_username
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        let successMsg = `Connected to ${page.page_name}!`;
        if (data.instagram?.connected) {
          successMsg += ` Instagram ${data.instagram.username} also linked!`;
        }
        toast.success(successMsg);
        setShowPageSelectModal(false);
        fetchSocialAccounts();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to connect page");
      }
    } catch (err) {
      toast.error("Error connecting page");
    } finally {
      setSelectingPage(false);
    }
  };

  const disconnectSocialAccount = async (platform) => {
    try {
      const res = await fetch(`${API}/social/accounts/disconnect`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ platform })
      });
      
      if (res.ok) {
        toast.success("Account disconnected");
        fetchSocialAccounts();
      }
    } catch (err) {
      toast.error("Error disconnecting account");
    }
  };

  // Send test post to verify OAuth is working
  const sendTestPost = async (platform) => {
    setTestingPlatform(platform);
    try {
      const res = await fetch(`${API}/ai/test-post`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ platform })
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        toast.success(`Test post sent to ${platform}! Check your ${platform} feed.`);
        if (data.post_id) {
          console.log(`${platform} post ID:`, data.post_id);
        }
      } else {
        toast.error(data.detail || `Failed to send test post to ${platform}`);
      }
    } catch (err) {
      toast.error(`Error sending test post: ${err.message}`);
    } finally {
      setTestingPlatform(null);
    }
  };

  const buyCredits = async (packId) => {
    setBuyingCredits(true);
    try {
      const originUrl = window.location.origin;
      
      const res = await fetch(`${API}/ai/credits/buy`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ pack_id: packId, origin_url: originUrl })
      });
      
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.checkout_url;
      } else {
        toast.error("Failed to start checkout");
        setBuyingCredits(false);
      }
    } catch (err) {
      toast.error("Error starting checkout");
      setBuyingCredits(false);
    }
  };

  const redeemPromoCode = async () => {
    if (!promoCode.trim()) return;
    
    setRedeemingPromo(true);
    try {
      const res = await fetch(`${API}/ai/credits/redeem`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ code: promoCode })
      });
      
      if (res.ok) {
        const data = await res.json();
        setCredits(data.new_balance);
        toast.success(`${data.credits_added} credits added!`);
        setPromoCode("");
      } else {
        const error = await res.json();
        toast.error(error.detail || "Invalid promo code");
      }
    } catch (err) {
      toast.error("Error redeeming code");
    } finally {
      setRedeemingPromo(false);
    }
  };

  const generateContent = async () => {
    if (!prompt.trim()) {
      toast.error("Please enter a prompt");
      return;
    }
    
    const cost = mode === "image" ? IMAGE_COST : VIDEO_COST;
    if (credits < cost) {
      toast.error(`Insufficient credits. Need ${cost}, have ${credits}`);
      return;
    }
    
    setGenerating(true);
    setGenerationProgress(0);
    setResult(null);
    
    // Start progress simulation for better UX
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    progressIntervalRef.current = setInterval(() => {
      setGenerationProgress(prev => {
        if (prev >= 90) return prev;
        const increment = mode === "image" ? 5 : 1;
        return Math.min(prev + increment, 90);
      });
    }, mode === "image" ? 500 : 3000);
    
    try {
      const endpoint = mode === "image" ? "/ai/generate/image" : "/ai/generate/video";
      const body = mode === "image" 
        ? { prompt, style, aspect_ratio: aspectRatio }
        : { prompt, style, aspect_ratio: aspectRatio, duration: videoDuration, reference_image_id: uploadedImage?.upload_id || null };
      
      const res = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      
      if (res.ok) {
        const data = await res.json();
        if (mode === "image") {
          clearInterval(progressIntervalRef.current);
          setGenerationProgress(100);
          setResult(data.image_base64);
          setResultType("image");
          setGenerationId(data.generation_id);
          setCredits(data.new_balance);
          toast.success("Image generated!");
          fetchHistory();
          setTimeout(() => {
            setGenerating(false);
            setGenerationProgress(0);
          }, 500);
        } else {
          // Video uses async generation with polling
          clearInterval(progressIntervalRef.current);
          setGenerationId(data.generation_id);
          setCredits(data.new_balance);
          const refMsg = uploadedImage ? " with your uploaded image" : "";
          toast.info(`Video generation started${refMsg}! This typically takes 2-5 minutes...`, { duration: 10000 });
          pollVideoStatus(data.generation_id);
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || "Generation failed");
        clearInterval(progressIntervalRef.current);
        setGenerating(false);
        setGenerationProgress(0);
      }
    } catch (err) {
      toast.error("Error generating content");
      clearInterval(progressIntervalRef.current);
      setGenerating(false);
      setGenerationProgress(0);
    }
  };
  
  // Handle image upload for AI reference
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file (PNG, JPEG, or WEBP)");
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image too large. Maximum 5MB.");
      return;
    }
    
    setUploadingImage(true);
    
    try {
      // Read file as base64 for local use (image-to-video)
      const base64Promise = new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result.split(',')[1]; // Remove data:image/...;base64, prefix
          resolve(base64);
        };
        reader.readAsDataURL(file);
      });
      
      // Also upload to server
      const formData = new FormData();
      formData.append('file', file);
      
      const [base64Data, res] = await Promise.all([
        base64Promise,
        fetch(`${API}/ai/upload/image`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`
          },
          body: formData
        })
      ]);
      
      if (res.ok) {
        const data = await res.json();
        setUploadedImage({
          upload_id: data.upload_id,
          filename: data.filename,
          preview: URL.createObjectURL(file),
          base64: base64Data  // Store base64 for image-to-video
        });
        toast.success(`Uploaded: ${file.name}`);
      } else {
        const error = await res.json();
        toast.error(error.detail || "Upload failed");
      }
    } catch (err) {
      toast.error("Error uploading image");
    } finally {
      setUploadingImage(false);
    }
  };

  // Poll for video generation status
  const pollVideoStatus = async (genId, attempts = 0) => {
    const maxAttempts = 90; // 90 * 4s = 6 minutes max
    const pollInterval = 4000;
    
    if (attempts >= maxAttempts) {
      toast.error("Video generation timed out. Please check your generation history.");
      setGenerating(false);
      return;
    }
    
    try {
      const res = await fetch(`${API}/ai/generate/status/${genId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) {
        throw new Error("Failed to check status");
      }
      
      const data = await res.json();
      
      if (data.status === "completed" && (data.video_base64 || data.video_url)) {
        // Support both new URL-based and old base64-based video storage
        if (data.video_url) {
          setResult(data.video_url);
          setResultType("video_url");
        } else {
          setResult(data.video_base64);
          setResultType("video");
        }
        toast.success("Video generated successfully!");
        fetchHistory();
        setGenerating(false);
        return;
      } else if (data.status === "failed") {
        // Show user-friendly error message
        let errorMessage = data.user_message || data.error || "Video generation failed";
        
        // If credits were refunded, notify the user
        if (data.credits_refunded) {
          errorMessage += ` (${data.credits_refunded} credits refunded)`;
          fetchCredits(); // Refresh credit balance
        }
        
        if (data.error_type === "content_policy") {
          toast.error(errorMessage, { duration: 8000 });
        } else if (data.error_type === "timeout") {
          toast.error(errorMessage, { duration: 6000 });
        } else {
          toast.error(errorMessage);
        }
        setGenerating(false);
        fetchHistory(); // Refresh history to show failed status
        return;
      }
      
      // Still pending, continue polling
      if (attempts % 5 === 0) {
        toast.loading(`Generating video... (${Math.floor(attempts * 4 / 60)}m ${(attempts * 4) % 60}s)`, { id: "video-gen" });
      }
      setTimeout(() => pollVideoStatus(genId, attempts + 1), pollInterval);
    } catch (err) {
      console.error("Error polling video status:", err);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollVideoStatus(genId, attempts + 1), pollInterval);
      } else {
        toast.error("Error checking video status");
        setGenerating(false);
      }
    }
  };

  const downloadContent = () => {
    if (!result) return;
    
    const link = document.createElement("a");
    if (resultType === "image") {
      link.href = `data:image/png;base64,${result}`;
      link.download = `veyronix_ai_${Date.now()}.png`;
    } else if (resultType === "video_url") {
      // For URL-based videos, open in new tab or use the API download endpoint
      window.open(`${API}${result}`, '_blank');
      toast.success("Opening video...");
      return;
    } else {
      link.href = `data:video/mp4;base64,${result}`;
      link.download = `veyronix_ai_${Date.now()}.mp4`;
    }
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Downloaded!");
  };

  // TopView AI Generation Functions
  const generateTopviewContent = async () => {
    if (aiTool === "basic") {
      generateContent();
      return;
    }
    
    setGenerating(true);
    
    try {
      let endpoint = "";
      let body = {};
      
      switch (aiTool) {
        case "avatar":
          if (!uploadedImage) {
            toast.error("Please upload a photo first");
            setGenerating(false);
            return;
          }
          endpoint = "/topview/generate/avatar";
          body = { photo_base64: uploadedImage.base64, style: "professional" };
          break;
          
        case "product_shoot":
          if (!uploadedImage) {
            toast.error("Please upload a product image first");
            setGenerating(false);
            return;
          }
          endpoint = "/topview/generate/product-shoot";
          body = { product_image_base64: uploadedImage.base64, scene, style: "realistic" };
          break;
          
        case "lip_sync":
          if (!script.trim() && !prompt.trim()) {
            toast.error("Please enter a script");
            setGenerating(false);
            return;
          }
          endpoint = "/topview/generate/lip-sync";
          body = { script: script || prompt, voice, accent: "british" };
          break;
          
        case "face_swap":
          if (!uploadedImage) {
            toast.error("Please upload your photo for face swap");
            setGenerating(false);
            return;
          }
          endpoint = "/topview/generate/face-swap";
          body = { 
            source_image_base64: uploadedImage.base64,
            target_video_url: null // Will use demo video
          };
          break;
          
        case "image_to_video":
          if (!uploadedImage) {
            toast.error("Please upload an image first");
            setGenerating(false);
            return;
          }
          endpoint = "/topview/generate/image-to-video";
          body = { image_base64: uploadedImage.base64, motion_type: motionType, duration: 4 };
          break;
          
        case "viral_hooks":
          if (!selectedHook || !productName.trim()) {
            toast.error("Please select a template and enter product details");
            setGenerating(false);
            return;
          }
          endpoint = "/topview/generate/viral-hook";
          body = { 
            template_id: selectedHook, 
            product_name: productName, 
            key_benefit: keyBenefit || "saves time and money",
            target_audience: "UK merchants"
          };
          break;
          
        default:
          toast.error("Please select a tool");
          setGenerating(false);
          return;
      }
      
      const { ok, data } = await safeFetch(`${API}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      
      if (!ok) {
        throw new Error(data.detail || "Generation failed");
      }
      
      setTopviewResult(data);
      setShowTopviewResult(true);
      fetchCredits();
      
      if (data.image_base64) {
        setResult(data.image_base64);
        setResultType("image");
        toast.success("Generated successfully!");
      } else if (data.audio_base64) {
        toast.success("Audio generated!");
      } else if (data.script) {
        toast.success("Script generated!");
      } else if (data.video_url) {
        toast.success("Video ready!");
      }
      
    } catch (error) {
      toast.error(error.message || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const getToolCost = () => {
    switch (aiTool) {
      case "avatar": return AVATAR_COST;
      case "product_shoot": return PRODUCT_SHOOT_COST;
      case "lip_sync": return LIP_SYNC_COST;
      case "face_swap": return FACE_SWAP_COST;
      case "image_to_video": return IMAGE_TO_VIDEO_COST;
      case "viral_hooks": return VIRAL_HOOK_COST;
      default: return mode === "image" ? IMAGE_COST : VIDEO_COST;
    }
  };

  const postToSocial = async () => {
    if (!selectedPlatform || !result) return;
    
    setPosting(true);
    try {
      const { ok, data } = await safeFetch(`${API}/social/post`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          platform: selectedPlatform,
          content_type: resultType,
          content_base64: result,
          caption: postCaption,
          generation_id: generationId
        })
      });
      
      if (ok) {
        toast.success(data.message);
        setShowPostModal(false);
        setPostCaption("");
        setSelectedPlatform(null);
        fetchPostHistory();
      } else {
        toast.error(data.detail || "Posting failed");
      }
    } catch (err) {
      toast.error("Error posting content");
    } finally {
      setPosting(false);
    }
  };

  // Website Automation (Phase 5)
  const runWebsiteAutomation = async () => {
    if (!websiteUrl.trim()) {
      toast.error("Please enter a website URL");
      return;
    }
    
    if (credits < AUTOMATION_COST) {
      toast.error(`Insufficient credits. Need ${AUTOMATION_COST}, have ${credits}`);
      return;
    }
    
    setAutomating(true);
    setAutomationStep(1);
    setAutomationResult(null);
    
    // Simulate automation steps (mocked for demo)
    const steps = [
      "Connecting to website...",
      "Logging in to merchant site...",
      "Recording homepage...",
      "Navigating to products...",
      "Recording checkout flow...",
      "Taking screenshots...",
      "Generating social posts..."
    ];
    
    try {
      // Simulate step-by-step progress
      for (let i = 0; i < steps.length; i++) {
        setAutomationStep(i + 1);
        toast.loading(steps[i], { id: "automation" });
        await new Promise(resolve => setTimeout(resolve, 1200));
      }
      
      // Call backend to deduct credits and generate content
      const res = await fetch(`${API}/ai/automate/website`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          website_url: websiteUrl,
          login_email: loginEmail,
          login_password: loginPassword
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setAutomationResult(data);
        setCredits(data.new_balance);
        toast.success("Website automation complete!", { id: "automation" });
      } else {
        const error = await res.json();
        toast.error(error.detail || "Automation failed", { id: "automation" });
      }
    } catch (err) {
      toast.error("Error during automation", { id: "automation" });
    } finally {
      setAutomating(false);
      setAutomationStep(0);
    }
  };

  // Campaign Manager (Phase 5)
  const createCampaign = async () => {
    if (!campaignName.trim()) {
      toast.error("Please enter a campaign name");
      return;
    }
    
    if (credits < CAMPAIGN_COST) {
      toast.error(`Insufficient credits. Need ${CAMPAIGN_COST}, have ${credits}`);
      return;
    }
    
    setCreatingCampaign(true);
    setCampaignResult(null);
    
    try {
      toast.loading("Creating your 5-post campaign...", { id: "campaign" });
      
      const res = await fetch(`${API}/ai/campaign/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: campaignName,
          goal: campaignGoal
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setCampaignResult(data);
        setCredits(data.new_balance);
        toast.success("Campaign created!", { id: "campaign" });
      } else {
        const error = await res.json();
        toast.error(error.detail || "Campaign creation failed", { id: "campaign" });
      }
    } catch (err) {
      toast.error("Error creating campaign", { id: "campaign" });
    } finally {
      setCreatingCampaign(false);
    }
  };

  const connectedPlatforms = Object.values(socialAccounts).filter(a => a.connected);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-gray-900 dark:to-gray-950 text-gray-900 dark:text-white">
      {/* Header - Brand Gradient */}
      <div className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 dark:from-indigo-500 dark:via-violet-500 dark:to-fuchsia-400 px-6 py-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate("/merchant")}
              className="p-2 rounded-lg hover:bg-white/20 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-white" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-fuchsia-200" />
                Veyronix AI Studio
              </h1>
              <p className="text-indigo-100 text-sm">AI Video & Content Studio</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              {connectedPlatforms.map(account => {
                const Icon = PLATFORM_ICONS[account.platform];
                return (
                  <div 
                    key={account.platform}
                    className="p-2 rounded-lg bg-white/20"
                    title={`${account.name}: ${account.username}`}
                  >
                    <Icon className="h-4 w-4 text-white" style={{ color: account.color }} />
                  </div>
                );
              })}
              <button
                onClick={() => setShowConnectModal(true)}
                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 transition-colors"
                title="Connect accounts"
              >
                <Link2 className="h-4 w-4 text-white" />
              </button>
            </div>
            
            <div className="bg-white/20 border border-white/30 rounded-xl px-4 py-2 flex items-center gap-2">
              <Coins className="h-5 w-5 text-amber-300" />
              <span className="text-white font-bold">{loadingCredits ? "..." : credits}</span>
              <span className="text-white/70 text-sm">credits</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation - ENHANCED with new tabs */}
      <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm border-b border-gray-200 dark:border-gray-800 px-6 overflow-x-auto shadow-sm">
        <div className="max-w-7xl mx-auto flex gap-1 py-3">
          <button
            onClick={() => setActiveTab("generate")}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
              activeTab === "generate"
                ? "bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 text-white"
                : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800"
            }`}
            data-testid="tab-generate"
          >
            <Sparkles className="h-4 w-4" />
            Veyronix AI
            <span className="text-xs px-1.5 py-0.5 rounded bg-white/20 text-white">PRO</span>
          </button>
          <button
            onClick={() => setActiveTab("library")}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
              activeTab === "library"
                ? "bg-gradient-to-r from-emerald-600 to-teal-600 text-white"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            }`}
            data-testid="tab-library"
          >
            <FolderOpen className="h-4 w-4" />
            Library
            {libraryTotal > 0 && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-white/20">{libraryTotal}</span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("trending")}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
              activeTab === "trending"
                ? "bg-gradient-to-r from-rose-600 to-pink-600 text-white"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            }`}
            data-testid="tab-trending"
          >
            <TrendingUp className="h-4 w-4" />
            Trending
            <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700">FREE</span>
          </button>
          <button
            onClick={() => setActiveTab("abtester")}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
              activeTab === "abtester"
                ? "bg-gradient-to-r from-violet-600 to-indigo-600 text-white"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            }`}
            data-testid="tab-abtester"
          >
            <FlaskConical className="h-4 w-4" />
            A/B Test
          </button>
          <button
            onClick={() => setActiveTab("scheduler")}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
              activeTab === "scheduler"
                ? "bg-gradient-to-r from-blue-600 to-cyan-600 text-white"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            }`}
            data-testid="tab-scheduler"
          >
            <CalendarClock className="h-4 w-4" />
            Schedule
            {scheduledPosts.length > 0 && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-white/20">{scheduledPosts.length}</span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("analyzer")}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
              activeTab === "analyzer"
                ? "bg-gradient-to-r from-amber-600 to-orange-600 text-white"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            }`}
            data-testid="tab-analyzer"
          >
            <FileSearch className="h-4 w-4" />
            Competitor
          </button>
          <button
            onClick={() => setActiveTab("campaign")}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
              activeTab === "campaign"
                ? "bg-gradient-to-r from-orange-600 to-pink-600 text-white"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            }`}
            data-testid="tab-campaign"
          >
            <Megaphone className="h-4 w-4" />
            Campaigns
          </button>
          <button
            onClick={() => setActiveTab("agency")}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
              activeTab === "agency"
                ? "bg-gradient-to-r from-yellow-500 to-amber-600 text-white"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            }`}
            data-testid="tab-agency"
          >
            <Crown className="h-4 w-4" />
            Agency
            <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">PRO</span>
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Generate Content Tab - MERGED VEYRONIX AI */}
        {activeTab === "generate" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {/* AI Tool Selector */}
              <div className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 rounded-2xl p-1">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-indigo-600" />
                      Veyronix AI Studio
                    </h2>
                    <div className="text-sm text-slate-500">
                      Cost: <span className="font-bold text-indigo-600">{getToolCost()} cr</span>
                    </div>
                  </div>
                  
                  {/* Tool Selection Grid */}
                  <div className="grid grid-cols-4 md:grid-cols-7 gap-2 mb-4">
                    {[
                      { id: "basic", icon: Sparkles, label: "Generate", desc: "Image/Video" },
                      { id: "avatar", icon: User, label: "Avatar", desc: "AI Face" },
                      { id: "product_shoot", icon: Camera, label: "Product", desc: "Scene" },
                      { id: "lip_sync", icon: Mic, label: "Lip Sync", desc: "Voice" },
                      { id: "face_swap", icon: RefreshCw, label: "Face Swap", desc: "Video" },
                      { id: "image_to_video", icon: PlayCircle, label: "Img→Video", desc: "Motion" },
                      { id: "viral_hooks", icon: TrendingUp, label: "Viral", desc: "Scripts" },
                    ].map(tool => {
                      const Icon = tool.icon;
                      return (
                        <button
                          key={tool.id}
                          onClick={() => !tool.disabled && setAiTool(tool.id)}
                          disabled={tool.disabled}
                          className={`p-3 rounded-xl text-center transition-all ${
                            aiTool === tool.id
                              ? "bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg scale-105"
                              : tool.disabled
                                ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                                : "bg-slate-50 hover:bg-slate-100 text-slate-700 hover:scale-102"
                          }`}
                          data-testid={`tool-${tool.id}`}
                        >
                          <Icon className={`h-5 w-5 mx-auto mb-1 ${aiTool === tool.id ? "text-white" : ""}`} />
                          <div className="text-xs font-semibold truncate">{tool.label}</div>
                          <div className={`text-[10px] ${aiTool === tool.id ? "text-white/70" : "text-slate-500"}`}>{tool.desc}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl">
                {/* Basic Generate Mode Selector */}
                {aiTool === "basic" && (
                  <div className="flex gap-4 mb-6">
                    <button
                      onClick={() => setMode("image")}
                      className={`flex-1 py-4 rounded-xl flex items-center justify-center gap-3 font-semibold transition-all ${
                        mode === "image"
                          ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg"
                          : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                      }`}
                      data-testid="mode-image"
                    >
                      <ImageIcon className="h-5 w-5" />
                      Image
                      <span className={`text-xs px-2 py-0.5 rounded-full ${mode === "image" ? "bg-white/20" : "bg-slate-200"}`}>
                        {IMAGE_COST} credits
                      </span>
                    </button>
                    <button
                      onClick={() => setMode("video")}
                      className={`flex-1 py-4 rounded-xl flex items-center justify-center gap-3 font-semibold transition-all ${
                        mode === "video"
                          ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg"
                          : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                      }`}
                      data-testid="mode-video"
                    >
                      <Video className="h-5 w-5" />
                      Video
                      <span className={`text-xs px-2 py-0.5 rounded-full ${mode === "video" ? "bg-white/20" : "bg-slate-200"}`}>
                        {VIDEO_COST} credits
                      </span>
                    </button>
                  </div>
                )}

                {/* TopView Tool-Specific Controls */}
                {aiTool !== "basic" && (
                  <div className="space-y-4 mb-6">
                    {/* Avatar & Product Shoot - Image Upload */}
                    {(aiTool === "avatar" || aiTool === "product_shoot" || aiTool === "image_to_video") && (
                      <div className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center hover:border-indigo-400 transition-colors">
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleImageUpload}
                          accept="image/*"
                          className="hidden"
                        />
                        {uploadedImage ? (
                          <div className="space-y-3">
                            <img src={uploadedImage.preview} alt="Upload" className="max-h-32 mx-auto rounded-lg" />
                            <Button variant="outline" size="sm" onClick={() => setUploadedImage(null)}>
                              <X className="h-4 w-4 mr-1" /> Remove
                            </Button>
                          </div>
                        ) : (
                          <div onClick={() => fileInputRef.current?.click()} className="cursor-pointer">
                            <Upload className="h-10 w-10 mx-auto text-slate-400 mb-2" />
                            <p className="text-slate-600 font-medium">
                              {aiTool === "avatar" ? "Upload your photo" : 
                               aiTool === "product_shoot" ? "Upload product PNG" : 
                               "Upload static image"}
                            </p>
                            <p className="text-sm text-slate-500">PNG or JPG, max 5MB</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Product Shoot - Scene Selection */}
                    {aiTool === "product_shoot" && (
                      <div>
                        <Label className="text-slate-700 font-medium">Scene</Label>
                        <Select value={scene} onValueChange={setScene}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="store">Retail Store</SelectItem>
                            <SelectItem value="office">Modern Office</SelectItem>
                            <SelectItem value="outdoor">Outdoor Urban</SelectItem>
                            <SelectItem value="cafe">Cafe Setting</SelectItem>
                            <SelectItem value="home">Home Interior</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Image to Video - Motion Selection */}
                    {aiTool === "image_to_video" && (
                      <div>
                        <Label className="text-slate-700 font-medium">Motion Type</Label>
                        <Select value={motionType} onValueChange={setMotionType}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="zoom">Zoom In</SelectItem>
                            <SelectItem value="pan">Pan Left/Right</SelectItem>
                            <SelectItem value="rotate">Subtle Rotate</SelectItem>
                            <SelectItem value="parallax">Parallax Effect</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Lip Sync - Script & Voice */}
                    {aiTool === "lip_sync" && (
                      <>
                        <div>
                          <Label className="text-slate-700 font-medium">Script (what the avatar will say)</Label>
                          <Textarea 
                            value={script || prompt}
                            onChange={(e) => setScript(e.target.value)}
                            placeholder="Hi, I'm using Veyronix - the UK's number one merchant platform..."
                            rows={3}
                          />
                        </div>
                        <div>
                          <Label className="text-slate-700 font-medium">Voice</Label>
                          <Select value={voice} onValueChange={setVoice}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="nova">Nova (UK Female)</SelectItem>
                              <SelectItem value="onyx">Onyx (UK Male)</SelectItem>
                              <SelectItem value="alloy">Alloy (Neutral)</SelectItem>
                              <SelectItem value="shimmer">Shimmer (Friendly)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </>
                    )}

                    {/* Face Swap - Photo Upload */}
                    {aiTool === "face_swap" && (
                      <div className="space-y-4">
                        <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-xl border border-purple-200">
                          <h4 className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
                            <RefreshCw className="h-4 w-4" />
                            Face Swap with Google AI
                          </h4>
                          <p className="text-sm text-purple-600">Upload your photo to swap into a professional demo video</p>
                        </div>
                        <div className="border-2 border-dashed border-purple-300 rounded-xl p-6 text-center hover:border-purple-400 transition-colors bg-purple-50/30">
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleImageUpload}
                            accept="image/*"
                            className="hidden"
                          />
                          {uploadedImage ? (
                            <div className="space-y-3">
                              <img src={uploadedImage.preview} alt="Your photo" className="max-h-40 mx-auto rounded-lg shadow-md" />
                              <p className="text-sm text-purple-600 font-medium">Your face will be swapped into the demo video</p>
                              <Button variant="outline" size="sm" onClick={() => setUploadedImage(null)}>
                                <X className="h-4 w-4 mr-1" /> Change Photo
                              </Button>
                            </div>
                          ) : (
                            <div onClick={() => fileInputRef.current?.click()} className="cursor-pointer">
                              <User className="h-12 w-12 mx-auto text-purple-400 mb-3" />
                              <p className="text-purple-700 font-medium">Upload your photo</p>
                              <p className="text-sm text-purple-500">Clear face photo works best</p>
                            </div>
                          )}
                        </div>
                        <div className="bg-slate-50 p-3 rounded-lg">
                          <p className="text-xs text-slate-500 text-center">
                            Powered by <span className="font-semibold">Google AI Studio</span> • Your face → Professional merchant demo
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Viral Hooks - Template Selection */}
                    {aiTool === "viral_hooks" && (
                      <>
                        <div>
                          <Label className="text-slate-700 font-medium">Select Viral Template</Label>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 max-h-40 overflow-y-auto">
                            {viralHooks.slice(0, 12).map((hook) => (
                              <div
                                key={hook.id}
                                onClick={() => setSelectedHook(hook.id)}
                                className={`p-2 rounded-lg border cursor-pointer transition-all text-sm ${
                                  selectedHook === hook.id 
                                    ? "border-indigo-500 bg-indigo-50" 
                                    : "border-slate-200 hover:border-indigo-300"
                                }`}
                              >
                                <p className="font-medium truncate">{hook.name}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-slate-700 font-medium">Product Name</Label>
                            <Input 
                              value={productName}
                              onChange={(e) => setProductName(e.target.value)}
                              placeholder="Veyronix POS System"
                            />
                          </div>
                          <div>
                            <Label className="text-slate-700 font-medium">Key Benefit</Label>
                            <Input 
                              value={keyBenefit}
                              onChange={(e) => setKeyBenefit(e.target.value)}
                              placeholder="Saves 5 hours per week"
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Common Prompt Input (for basic and some tools) */}
                {(aiTool === "basic" || aiTool === "avatar" || aiTool === "product_shoot") && (
                <div className="space-y-4">
                  <div>
                    <Label className="text-gray-700 dark:text-gray-300 dark:text-gray-300 font-medium">Describe what you want to create</Label>
                    <Textarea
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder={aiTool === "avatar" 
                        ? "e.g., Professional business owner avatar for social media marketing"
                        : aiTool === "product_shoot"
                          ? "e.g., POS terminal on a modern retail counter with warm lighting"
                          : mode === "image" 
                            ? "e.g., A sleek product photo of headphones on a minimalist desk with soft lighting"
                            : "e.g., A smooth camera pan across a modern coffee shop with customers enjoying drinks"
                      }
                      className="mt-2 min-h-[120px] bg-gray-50 dark:bg-gray-700 dark:bg-gray-800 border-gray-200 dark:border-gray-600 focus:ring-purple-500 dark:text-white"
                      data-testid="prompt-input"
                    />
                    
                    {/* Upload Logo Section - Video Mode Only */}
                    {mode === "video" && (
                      <div 
                        className={`mt-3 p-4 border-2 border-dashed rounded-xl transition-all ${
                          uploadedImage 
                            ? 'border-green-300 bg-green-50' 
                            : 'border-gray-300 bg-gray-50 dark:bg-gray-700 hover:border-purple-400 hover:bg-purple-50'
                        }`}
                        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-purple-500', 'bg-purple-100'); }}
                        onDragLeave={(e) => { e.currentTarget.classList.remove('border-purple-500', 'bg-purple-100'); }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.currentTarget.classList.remove('border-purple-500', 'bg-purple-100');
                          const file = e.dataTransfer.files[0];
                          if (file && file.type.startsWith('image/')) {
                            const fakeEvent = { target: { files: [file] } };
                            handleImageUpload(fakeEvent);
                          }
                        }}
                      >
                        <input
                          ref={uploadInputRef}
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                        {uploadedImage ? (
                          <div className="flex items-center gap-4">
                            <div className="relative">
                              <img 
                                src={uploadedImage.preview} 
                                alt="Logo preview" 
                                className="w-16 h-16 rounded-lg object-cover border-2 border-green-400 shadow-sm" 
                              />
                              <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                                <Check className="h-4 w-4 text-white" />
                              </div>
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-green-700 flex items-center gap-1">
                                <Check className="h-4 w-4" />
                                Image ready!
                              </p>
                              <p className="text-xs text-green-600">{uploadedImage.filename}</p>
                            </div>
                            <Button 
                              variant="ghost"
                              size="sm"
                              onClick={() => setUploadedImage(null)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <X className="h-4 w-4 mr-1" />
                              Remove
                            </Button>
                          </div>
                        ) : (
                          <div 
                            className="flex flex-col items-center gap-2 cursor-pointer"
                            onClick={() => uploadInputRef.current?.click()}
                          >
                            {uploadingImage ? (
                              <Loader2 className="h-8 w-8 text-purple-500 animate-spin" />
                            ) : (
                              <Upload className="h-8 w-8 text-gray-400" />
                            )}
                            <p className="text-sm text-gray-600 font-medium">
                              {uploadingImage ? 'Uploading...' : 'Drop logo here or click to upload'}
                            </p>
                            <p className="text-xs text-gray-400">PNG, JPG, WebP (will appear in video)</p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Magic Prompt Button */}
                    <div className="mt-2 flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={previewPrompt}
                        className="text-blue-600 border-blue-200 hover:bg-blue-50 hover:border-blue-300"
                        data-testid="preview-prompt-btn"
                        disabled={!prompt.trim()}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Preview AI Prompt
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowMagicModal(true)}
                        className="text-purple-600 border-purple-200 hover:bg-purple-50 hover:border-purple-300"
                        data-testid="magic-prompt-btn"
                      >
                        <Wand2 className="h-4 w-4 mr-1" />
                        ✨ Magic Prompt
                        <span className="ml-1 text-xs text-purple-400">({MAGIC_PROMPT_COST}cr)</span>
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-gray-700 dark:text-gray-300 text-sm">Style</Label>
                      <Select value={style} onValueChange={setStyle}>
                        <SelectTrigger className="mt-1 bg-gray-50 dark:bg-gray-700 border-gray-200">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="professional">Professional</SelectItem>
                          <SelectItem value="promotional">Promotional</SelectItem>
                          <SelectItem value="cartoon">Cartoon</SelectItem>
                          <SelectItem value="realistic">Realistic</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label className="text-gray-700 dark:text-gray-300 text-sm">Aspect Ratio</Label>
                      <Select value={aspectRatio} onValueChange={setAspectRatio}>
                        <SelectTrigger className="mt-1 bg-gray-50 dark:bg-gray-700 border-gray-200">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1:1">1:1 (Square)</SelectItem>
                          <SelectItem value="16:9">16:9 (Landscape)</SelectItem>
                          <SelectItem value="9:16">9:16 (Portrait)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {mode === "video" && (
                      <div>
                        <Label className="text-gray-700 dark:text-gray-300 text-sm">Duration</Label>
                        <Select value={String(videoDuration)} onValueChange={(v) => setVideoDuration(parseInt(v))}>
                          <SelectTrigger className="mt-1 bg-gray-50 dark:bg-gray-700 border-gray-200">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="4">4 seconds</SelectItem>
                            <SelectItem value="8">8 seconds</SelectItem>
                            <SelectItem value="12">12 seconds (Max)</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500 mt-1">Sora 2 supports up to 12 seconds</p>
                      </div>
                    )}
                  </div>
                </div>
                )}

                <Button
                  onClick={generateTopviewContent}
                  disabled={generating || (aiTool === "basic" && !prompt.trim()) || credits < getToolCost()}
                  className="w-full py-6 text-lg bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500 hover:from-indigo-700 hover:via-violet-700 hover:to-fuchsia-600 shadow-lg relative overflow-hidden"
                  data-testid="generate-btn"
                >
                    {generating && generationProgress > 0 && (
                      <div 
                        className="absolute inset-0 bg-white/20 transition-all duration-300"
                        style={{ width: `${generationProgress}%` }}
                      />
                    )}
                    <span className="relative flex items-center justify-center">
                    {generating ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        {aiTool === "basic" && mode === "video" 
                          ? "Generating (2-5 min)..." 
                          : generationProgress > 0 
                            ? `Generating... ${generationProgress}%`
                            : "Generating..."}
                      </>
                    ) : (
                      <>
                        <Zap className="h-5 w-5 mr-2" />
                        {aiTool === "basic" 
                          ? `Generate ${mode === "image" ? "Image" : "Video"}`
                          : aiTool === "viral_hooks" 
                            ? "Generate Viral Script"
                            : aiTool === "lip_sync"
                              ? "Generate Voice"
                              : `Generate ${aiTool.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")}`
                        }
                        <span className="ml-2 text-sm opacity-80">({getToolCost()} cr)</span>
                      </>
                    )}
                    </span>
                </Button>
              </div>

              {/* TopView Result Preview */}
              {showTopviewResult && topviewResult && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border-2 border-indigo-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-800 dark:text-white flex items-center gap-2">
                      <Check className="h-5 w-5 text-green-500" />
                      Veyronix AI Result
                    </h3>
                    <Button variant="outline" size="sm" onClick={() => setShowTopviewResult(false)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {topviewResult.image_base64 && (
                    <img 
                      src={`data:image/png;base64,${topviewResult.image_base64}`}
                      alt="Generated"
                      className="w-full rounded-lg mb-4"
                    />
                  )}
                  {topviewResult.image_url && (
                    <img 
                      src={topviewResult.image_url}
                      alt="Generated"
                      className="w-full rounded-lg mb-4"
                    />
                  )}
                  {topviewResult.video_url && (
                    <video 
                      src={topviewResult.video_url}
                      controls
                      className="w-full rounded-lg mb-4"
                    />
                  )}
                  {topviewResult.audio_base64 && (
                    <div className="bg-slate-50 p-4 rounded-lg mb-4">
                      <div className="flex items-center gap-3 mb-3">
                        <Mic className="h-5 w-5 text-indigo-600" />
                        <span className="font-medium">Generated Audio (UK Voice)</span>
                      </div>
                      <audio 
                        src={`data:audio/mp3;base64,${topviewResult.audio_base64}`}
                        controls
                        className="w-full"
                      />
                    </div>
                  )}
                  {topviewResult.script && (
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <h4 className="font-semibold mb-2 flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-indigo-600" />
                        Generated Viral Script
                      </h4>
                      <pre className="whitespace-pre-wrap text-sm text-slate-700 font-mono">{topviewResult.script}</pre>
                    </div>
                  )}
                  {topviewResult.message && (
                    <p className="text-sm text-slate-600 mt-3">{topviewResult.message}</p>
                  )}
                </div>
              )}

              {/* Result Preview */}
              {result && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-800 dark:text-white flex items-center gap-2">
                      <Check className="h-5 w-5 text-green-500" />
                      Generated Content
                    </h3>
                    <div className="flex gap-2">
                      <Button onClick={downloadContent} variant="outline" className="border-gray-200">
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </Button>
                      <Button 
                        onClick={() => setShowPostModal(true)}
                        className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
                      >
                        <Share2 className="h-4 w-4 mr-2" />
                        Post to Social
                      </Button>
                    </div>
                  </div>
                  
                  <div className="rounded-xl overflow-hidden bg-gray-100 flex items-center justify-center">
                    {resultType === "image" ? (
                      <img 
                        src={`data:image/png;base64,${result}`} 
                        alt="Generated" 
                        className="max-w-full max-h-[500px] object-contain"
                      />
                    ) : resultType === "video_url" ? (
                      <video 
                        src={`${API}${result}`}
                        controls
                        className="max-w-full max-h-[500px]"
                      />
                    ) : (
                      <video 
                        src={`data:video/mp4;base64,${result}`}
                        controls
                        className="max-w-full max-h-[500px]"
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Generation History */}
              {history.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl">
                  <h3 className="font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                    <Clock className="h-5 w-5 text-gray-400" />
                    Recent Generations
                  </h3>
                  <div className="space-y-3">
                    {history.slice(0, 5).map((gen) => (
                      <div key={gen.generation_id} className="flex items-center gap-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-700">
                        <div className={`p-2 rounded-lg ${gen.type === "image" ? "bg-purple-100" : "bg-pink-100"}`}>
                          {gen.type === "image" ? (
                            <ImageIcon className="h-4 w-4 text-purple-600" />
                          ) : (
                            <Video className="h-4 w-4 text-pink-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-700 dark:text-gray-300 truncate">{gen.prompt}</p>
                          <p className="text-xs text-gray-400">
                            {new Date(gen.created_at).toLocaleDateString("en-GB", {
                              day: "numeric",
                              month: "short",
                              hour: "2-digit",
                              minute: "2-digit"
                            })} · {gen.credits_used} credits
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Connected Accounts Card */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl">
                <h3 className="font-semibold text-gray-800 dark:text-white dark:text-white mb-4 flex items-center gap-2">
                  <Link2 className="h-5 w-5 text-blue-500" />
                  Connected Accounts
                </h3>
                <div className="space-y-3">
                  {Object.values(socialAccounts).map((account) => {
                    const Icon = PLATFORM_ICONS[account.platform] || Share2;
                    return (
                      <div key={account.platform} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700 dark:bg-gray-700">
                        <div 
                          className="p-2 rounded-lg"
                          style={{ backgroundColor: account.connected ? `${account.color}20` : "#f1f5f9" }}
                        >
                          <Icon 
                            className="h-5 w-5" 
                            style={{ color: account.connected ? account.color : "#94a3b8" }} 
                          />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-700 dark:text-gray-300 dark:text-gray-200">{account.name}</p>
                          {account.connected && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">{account.username}</p>
                          )}
                        </div>
                        {account.connected ? (
                          <div className="flex items-center gap-1">
                            <button 
                              onClick={() => sendTestPost(account.platform)}
                              disabled={testingPlatform === account.platform}
                              className="text-xs px-2 py-1 rounded bg-green-100 text-green-700 hover:bg-green-200 disabled:opacity-50"
                              title="Send test post"
                            >
                              {testingPlatform === account.platform ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                "Test"
                              )}
                            </button>
                            <button 
                              onClick={() => disconnectSocialAccount(account.platform)}
                              className="text-xs text-red-500 hover:text-red-600 p-1"
                            >
                              <Unlink className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => connectSocialAccount(account.platform)}
                            disabled={connectingPlatform === account.platform}
                            className="text-xs"
                          >
                            {connectingPlatform === account.platform ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              "Connect"
                            )}
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Buy Credits */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl">
                <h3 className="font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-purple-500" />
                  Buy Credits
                </h3>
                
                <div className="space-y-4">
                  <Select
                    value={selectedPack}
                    onValueChange={setSelectedPack}
                  >
                    <SelectTrigger className="w-full bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600" data-testid="credits-dropdown">
                      <SelectValue placeholder="Select a credit pack" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(packs).map(([packId, pack]) => (
                        <SelectItem key={packId} value={packId}>
                          {pack.credits} Credits - {pack.display_price}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Button
                    onClick={() => selectedPack && buyCredits(selectedPack)}
                    disabled={!selectedPack || buyingCredits}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                    data-testid="buy-credits-btn"
                  >
                    {buyingCredits ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard className="h-4 w-4 mr-2" />
                        Buy Credits
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Promo Code */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl">
                <h3 className="font-semibold text-gray-800 dark:text-white dark:text-white mb-4 flex items-center gap-2">
                  <Gift className="h-5 w-5 text-green-500" />
                  Redeem Promo Code
                </h3>
                
                <div className="flex gap-2">
                  <Input
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    placeholder="Enter code"
                    className="bg-gray-50 dark:bg-gray-700 border-gray-200"
                    data-testid="promo-input"
                  />
                  <Button
                    onClick={redeemPromoCode}
                    disabled={redeemingPromo || !promoCode.trim()}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="redeem-btn"
                  >
                    {redeemingPromo ? <Loader2 className="h-4 w-4 animate-spin" /> : "Redeem"}
                  </Button>
                </div>
              </div>

              {/* Pricing Info */}
              <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl p-6 text-white">
                <h3 className="font-semibold mb-4">Credit Usage</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ImageIcon className="h-4 w-4" />
                      <span>1 Image</span>
                    </div>
                    <span className="font-bold">{IMAGE_COST} credits</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Video className="h-4 w-4" />
                      <span>1 Video</span>
                    </div>
                    <span className="font-bold">{VIDEO_COST} credits</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Bot className="h-4 w-4" />
                      <span>Website Automation</span>
                    </div>
                    <span className="font-bold">{AUTOMATION_COST} credits</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Megaphone className="h-4 w-4" />
                      <span>5-Post Campaign</span>
                    </div>
                    <span className="font-bold">{CAMPAIGN_COST} credits</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Website Automation Tab */}
        {activeTab === "automate" && (
          <AutomateTab
            websiteUrl={websiteUrl} setWebsiteUrl={setWebsiteUrl}
            loginEmail={loginEmail} setLoginEmail={setLoginEmail}
            loginPassword={loginPassword} setLoginPassword={setLoginPassword}
            automating={automating} automationStep={automationStep} automationResult={automationResult}
            onRunAutomation={runWebsiteAutomation}
            credits={credits} AUTOMATION_COST={AUTOMATION_COST}
          />
        )}

        {/* Campaign Manager Tab */}
        {activeTab === "campaign" && (
          <CampaignTab
            campaignName={campaignName} setCampaignName={setCampaignName}
            campaignGoal={campaignGoal} setCampaignGoal={setCampaignGoal}
            creatingCampaign={creatingCampaign} campaignResult={campaignResult}
            onCreateCampaign={createCampaign}
            credits={credits} CAMPAIGN_COST={CAMPAIGN_COST}
          />
        )}

        {/* Library Tab */}
        {activeTab === "library" && (
          <LibraryTab
            library={library}
            libraryLoading={libraryLoading}
            libraryFilter={libraryFilter}
            setLibraryFilter={setLibraryFilter}
            libraryPage={libraryPage}
            setLibraryPage={setLibraryPage}
            libraryTotal={libraryTotal}
            libraryTotalPages={libraryTotalPages}
            onDownload={downloadFromLibrary}
            onSchedule={(item) => {
              setSelectedScheduleItem(item);
              setActiveTab("scheduler");
            }}
            onDelete={deleteGeneration}
            onNavigateToGenerate={() => setActiveTab("generate")}
            token={token}
            API={API}
            onRefresh={fetchLibrary}
          />
        )}

        {/* TRENDING Tab - FREE daily posts */}
        {activeTab === "trending" && (
          <TrendingTab
            trendingPosts={trendingPosts}
            loadingTrending={loadingTrending}
            onUsePrompt={(post) => {
              setPrompt(post.caption + " " + post.hashtags.join(" "));
              setActiveTab("generate");
              toast.success("Prompt copied! Ready to generate.");
            }}
          />
        )}

        {/* A/B TESTER Tab */}
        {/* A/B TESTER Tab */}
        {activeTab === "abtester" && (
          <ABTesterTab
            abVariantA={abVariantA} setAbVariantA={setAbVariantA}
            abVariantB={abVariantB} setAbVariantB={setAbVariantB}
            runningAbTest={runningAbTest} abTestResults={abTestResults}
            onRunAbTest={runAbTest}
            credits={credits} AB_TEST_COST={AB_TEST_COST}
          />
        )}

        {/* SCHEDULER Tab */}
        {activeTab === "scheduler" && (
          <SchedulerTab
            scheduledPosts={scheduledPosts} setScheduledPosts={setScheduledPosts}
            SCHEDULER_COST={SCHEDULER_COST}
          />
        )}

        {/* COMPETITOR ANALYZER Tab */}
        {activeTab === "analyzer" && (
          <AnalyzerTab
            competitorUrl={competitorUrl} setCompetitorUrl={setCompetitorUrl}
            analyzing={analyzing} competitorAnalysis={competitorAnalysis}
            onAnalyzeCompetitor={analyzeCompetitor}
            onUsePost={(post) => {
              setPrompt(post.caption + " " + post.hashtags);
              setActiveTab("generate");
              toast.success("Beat-post prompt loaded!");
            }}
            credits={credits} ANALYZER_COST={ANALYZER_COST}
          />
        )}

        {/* AGENCY MODE Tab */}
        {activeTab === "agency" && (
          <AgencyTab
            isAgencyMode={isAgencyMode} setIsAgencyMode={setIsAgencyMode}
            agencyClients={agencyClients} setAgencyClients={setAgencyClients}
            setSelectedClient={setSelectedClient}
            credits={credits} setCredits={setCredits} AGENCY_COST={AGENCY_COST}
          />
        )}
      </div>

      {/* Post to Social Modal */}
      <Dialog open={showPostModal} onOpenChange={setShowPostModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="h-5 w-5 text-blue-500" />
              Post to Social Media
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-sm text-gray-600">Select Platform</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                {Object.values(socialAccounts).filter(a => a.connected).map((account) => {
                  const Icon = PLATFORM_ICONS[account.platform] || Share2;
                  return (
                    <button
                      key={account.platform}
                      onClick={() => setSelectedPlatform(account.platform)}
                      className={`p-3 rounded-xl border-2 flex items-center gap-2 transition-all ${
                        selectedPlatform === account.platform
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <Icon className="h-5 w-5" style={{ color: account.color }} />
                      <span className="text-sm font-medium">{account.name}</span>
                    </button>
                  );
                })}
              </div>
              {connectedPlatforms.length === 0 && (
                <p className="text-sm text-amber-600 mt-2">
                  Connect an account first to post content
                </p>
              )}
            </div>
            
            <div>
              <Label className="text-sm text-gray-600">Caption (optional)</Label>
              <Textarea
                value={postCaption}
                onChange={(e) => setPostCaption(e.target.value)}
                placeholder="Write a caption for your post..."
                className="mt-1"
                rows={3}
              />
            </div>
            
            <Button
              onClick={postToSocial}
              disabled={!selectedPlatform || posting}
              className="w-full bg-gradient-to-r from-blue-500 to-indigo-600"
            >
              {posting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Posting...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Post Now
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Connect Accounts Modal */}
      <Dialog open={showConnectModal} onOpenChange={setShowConnectModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-blue-500" />
              Connect Social Accounts
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3">
            {Object.values(socialAccounts).map((account) => {
              const Icon = PLATFORM_ICONS[account.platform] || Share2;
              const isLive = account.connection_type === "live";
              return (
                <div key={account.platform} className="flex items-center gap-3 p-4 rounded-xl bg-gray-50 dark:bg-gray-700">
                  <div 
                    className="p-3 rounded-xl"
                    style={{ backgroundColor: account.connected ? `${account.color}20` : "#e2e8f0" }}
                  >
                    <Icon 
                      className="h-6 w-6" 
                      style={{ color: account.connected ? account.color : "#94a3b8" }} 
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-gray-800 dark:text-white">{account.name}</p>
                      {account.connected && isLive && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">LIVE</span>
                      )}
                    </div>
                    {account.connected ? (
                      <p className="text-sm text-green-600">{account.username}</p>
                    ) : (
                      <p className="text-sm text-gray-500">Not connected</p>
                    )}
                  </div>
                  {account.connected ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => disconnectSocialAccount(account.platform)}
                      className="text-red-500 border-red-200 hover:bg-red-50"
                    >
                      Disconnect
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => connectSocialAccount(account.platform)}
                      disabled={connectingPlatform === account.platform}
                      className="bg-blue-500 hover:bg-blue-600"
                    >
                      {connectingPlatform === account.platform ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        "Connect"
                      )}
                    </Button>
                  )}
                </div>
              );
            })}
            <div className="text-xs text-gray-500 text-center pt-2 space-y-1">
              <p><span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs">LIVE</span> = Real OAuth connection</p>
              <p>All platforms use real OAuth integration</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Facebook Page Selection Modal */}
      <Dialog open={showPageSelectModal} onOpenChange={setShowPageSelectModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Facebook className="h-5 w-5 text-blue-600" />
              Select a Facebook Page
            </DialogTitle>
            {fbUserName && (
              <p className="text-sm text-gray-500">Logged in as {fbUserName}</p>
            )}
          </DialogHeader>
          
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {availablePages.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No Facebook Pages found.</p>
                <p className="text-sm mt-2">You need to be an admin of at least one Facebook Page.</p>
              </div>
            ) : (
              availablePages.map((page) => (
                <div 
                  key={page.page_id} 
                  className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50/50 cursor-pointer transition-colors"
                  onClick={() => !selectingPage && selectFacebookPage(page)}
                >
                  {page.page_picture ? (
                    <img src={page.page_picture} alt="" className="w-12 h-12 rounded-full" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                      <Facebook className="h-6 w-6 text-blue-600" />
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="font-semibold text-gray-800 dark:text-white">{page.page_name}</p>
                    <p className="text-sm text-gray-500">Facebook Page</p>
                    {page.instagram_username && (
                      <div className="flex items-center gap-1 mt-1">
                        <Instagram className="h-3 w-3 text-pink-500" />
                        <span className="text-xs text-pink-600">@{page.instagram_username} will also connect</span>
                      </div>
                    )}
                  </div>
                  <Button size="sm" disabled={selectingPage} className="bg-blue-600 hover:bg-blue-700">
                    {selectingPage ? <Loader2 className="h-4 w-4 animate-spin" /> : "Select"}
                  </Button>
                </div>
              ))
            )}
          </div>
          
          <div className="text-xs text-gray-500 text-center pt-2">
            Select a Page to post AI-generated content directly to Facebook & Instagram
          </div>
        </DialogContent>
      </Dialog>

      {/* Magic Prompt Modal */}
      <Dialog open={showMagicModal} onOpenChange={setShowMagicModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-purple-500" />
              ✨ Magic Prompt Generator
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-sm text-gray-600">What are you promoting?</Label>
              <Textarea
                value={magicPromptInput}
                onChange={(e) => setMagicPromptInput(e.target.value)}
                placeholder="e.g., selling handmade jewelry, opening a new coffee shop, launching a fitness app..."
                className="mt-2 min-h-[80px]"
                data-testid="magic-input"
              />
            </div>
            
            <Button
              onClick={generateMagicPrompt}
              disabled={generatingMagic || credits < MAGIC_PROMPT_COST}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600"
              data-testid="magic-generate-btn"
            >
              {generatingMagic ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating Magic...
                </>
              ) : (
                <>
                  <Wand2 className="h-4 w-4 mr-2" />
                  Transform into Viral Post ({MAGIC_PROMPT_COST} credits)
                </>
              )}
            </Button>

            {/* Magic Variants */}
            {magicPromptVariants.length > 0 && (
              <div className="space-y-3">
                <Label className="text-sm text-gray-600 font-medium">Pick your favorite variant:</Label>
                {magicPromptVariants.map((variant, idx) => (
                  <div 
                    key={idx}
                    className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-200 hover:border-purple-400 transition-colors cursor-pointer group"
                    onClick={() => selectMagicVariant(variant)}
                    data-testid={`magic-variant-${idx}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-medium text-purple-600 bg-purple-100 px-2 py-0.5 rounded">
                            Variant {idx + 1}
                          </span>
                          {variant.hook && (
                            <span className="text-xs text-gray-500">Hook: {variant.hook.substring(0, 30)}...</span>
                          )}
                        </div>
                        <p className="text-gray-800 dark:text-white text-sm whitespace-pre-wrap">{variant.caption}</p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {variant.hashtags?.map(tag => (
                            <span key={tag} className="text-xs text-blue-600">#{tag}</span>
                          ))}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        className="bg-purple-600 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          selectMagicVariant(variant);
                        }}
                      >
                        Use This
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Prompt Preview Modal */}
      <Dialog open={showPromptPreview} onOpenChange={setShowPromptPreview}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-blue-500" />
              AI Prompt Preview
            </DialogTitle>
          </DialogHeader>
          
          {promptPreview && (
            <div className="space-y-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <h4 className="font-medium text-blue-700 dark:text-blue-300 mb-2 flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  Quick Preview
                </h4>
                <p className="text-gray-700 dark:text-gray-300 text-sm">
                  {promptPreview.preview}
                </p>
              </div>
              
              <div className="border rounded-lg p-4">
                <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Full AI Prompt</h4>
                <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded text-xs font-mono text-gray-600 dark:text-gray-400 whitespace-pre-wrap max-h-48 overflow-y-auto">
                  {promptPreview.full_prompt}
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2">
                <span className="text-sm text-gray-500">Keywords:</span>
                {promptPreview.keywords?.map((kw, i) => (
                  <span key={i} className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-full text-xs">
                    {kw}
                  </span>
                ))}
              </div>
              
              <div className="flex items-center justify-between pt-2 border-t">
                <div className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-green-500" />
                  <span className="text-gray-600">Match Quality: <span className="font-medium text-green-600">High</span></span>
                </div>
                <Button
                  onClick={() => setShowPromptPreview(false)}
                  className="bg-gradient-to-r from-purple-600 to-pink-600"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Now
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AISocialPage;
